import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { EmailStreakPerWeek } from "features/Influencer/HomePage/Gamification/schema";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import WeeklyConsistencyChallengeLoading from "./WeeklyConssitencyChallengeLoading";
import styles from "./styles";

const _EMAIL_TARGET_FOR_PRO = 30;
const _EMAIL_TARGET_FOR_FREE = 10;

interface Props {
  completed?: boolean;
}

const WeeklyConsistencyChallenge = ({ completed }: Props) => {
  const navigate = useNavigate();
  const { currentOrg, gamificationProfile } = useContext(
    OrganizationUserContext,
  );
  const { profileLoading, refreshGamificationProfile } = useGamification();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const target = currentOrg?.isProUser
    ? _EMAIL_TARGET_FOR_PRO
    : _EMAIL_TARGET_FOR_FREE;

  if (profileLoading || !gamificationProfile) {
    return <WeeklyConsistencyChallengeLoading />;
  }
  const { emailStreaks, longestStreak } = gamificationProfile;
  const currentWeek = emailStreaks?.find(
    (streak: EmailStreakPerWeek) => streak.isCurrentWeek,
  );
  const displayActionText = target - (currentWeek?.emailCount || 0) > 0;

  return (
    <Box sx={styles.card(completed)}>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2 }}>
        {/* Dollar sign icon */}
        <Box sx={styles.dollarIcon}>
          <Typography sx={{ color: "#FFFFFF", fontSize: 20, fontWeight: 700 }}>
            $
          </Typography>
        </Box>

        {/* Title + chip */}
        <Box
          sx={{
            display: { xs: "auto", md: "flex" },
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: { xs: "auto", md: "flex" },
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box>
              <Typography fontWeight={700} component={"span"}>
                Weekly Consistency Challenge
              </Typography>

              {!completed && (
                <Button
                  size="small"
                  onClick={() => refreshGamificationProfile(true)}
                  disabled={profileLoading}
                  startIcon={
                    <Box component="i" className="fa-regular fa-rotate-right" />
                  }
                  sx={{
                    textTransform: "none",
                    color: "purple.dark",
                    fontSize: 10,
                  }}
                >
                  Refresh
                </Button>
              )}
            </Box>

            <Chip
              label={
                completed ? (
                  <>
                    <Box
                      component="i"
                      className="fa-regular fa-check"
                      sx={{ mr: 0.5 }}
                    />
                    $30 credit earned
                  </>
                ) : (
                  "Earn $30 site credit"
                )
              }
              size="small"
              sx={styles.earnCreditChip}
            />
          </Box>
          {currentOrg?.isProUser && (
            <Typography variant="body2" color="text.secondary">
              Send {target} pitches every week for 4 consecutive weeks to unlock
              $30 in site credit
            </Typography>
          )}
        </Box>
      </Box>

      {/* Info box */}
      <Box sx={styles.infoBox}>
        <Box
          component="i"
          className="fa-solid fa-arrow-trend-up"
          sx={styles.infoBoxIcon}
        />
        <Typography>
          <strong>The reality check:</strong> Most creators give up after 1-2
          weeks, but pitching is a numbers game. Brand deals are landed most
          often by creators who show up week after week. This challenge helps
          you to build that habit.
        </Typography>
      </Box>

      {/* This Week's Progress */}
      {!completed && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "20px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box
                component="i"
                className="fa-regular fa-calendar"
                sx={{ width: 16, height: 16, fontSize: 14, color: "#4A5565" }}
              />
              <Typography sx={styles.text}>This Week's Progress</Typography>
              {/* Refresh button */}
            </Box>
            <Typography sx={styles.pitchText}>
              {(currentWeek?.emailCount || 0) > target ? (
                <>Target achieved! 🎉</>
              ) : (
                <>
                  {currentWeek?.emailCount}/{target} pitches
                </>
              )}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#6A7282",
            }}
          >
            Week runs from {currentWeek?.startAt} - {currentWeek?.endAt}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={
              currentWeek?.completed
                ? 100
                : currentWeek
                  ? (currentWeek.emailCount / target) * 100
                  : 0
            }
            sx={styles.linearProgress}
          />
        </Box>
      )}

      {/* Consecutive Weeks Progress */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: "100%",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: "#4A5565",
              fontSize: "14px",
            }}
          >
            Consecutive Weeks
          </Typography>
          <Typography sx={styles.weekProgressText}>
            {completed && <>4/4 weeks</>}
            {!completed && (
              <>
                {longestStreak} / {Math.max(longestStreak!, 4)} weeks{" "}
              </>
            )}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={
            completed
              ? 100
              : (longestStreak! / Math.max(longestStreak!, 4)) * 100
          }
          sx={styles.linearProgress}
        />
      </Box>

      {/* Week Cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          width: "100%",
          mb: 2,
        }}
      >
        {emailStreaks?.map((week: EmailStreakPerWeek) => {
          const isFuture =
            !week.isCurrentWeek &&
            week.index > (currentWeek?.index ?? emailStreaks?.length);

          return (
            <Box
              key={week.index}
              sx={[
                styles.weekBox,
                week.completed && {
                  borderColor: "#22C55E",
                  backgroundColor: "#F0FDF4",
                },
                week.isCurrentWeek &&
                  !week.completed && {
                    borderColor: "#6366F1",
                    backgroundColor: "#EEF2FF",
                  },
              ]}
            >
              <Typography sx={styles.weekText}>
                Week {isMobileScreen ? week.index : week.startAt}
              </Typography>
              {isFuture ? (
                <Box
                  component="i"
                  className="fa-solid fa-lock"
                  sx={{ fontSize: "14px", color: "#9CA3AF" }}
                />
              ) : week.completed ? (
                <Box
                  component="i"
                  className="fa-solid fa-circle-check"
                  sx={{ fontSize: "14px", color: "#22C55E" }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: week.isCurrentWeek ? "#6366F1" : "#4A5565",
                    lineHeight: 1,
                  }}
                >
                  {week.emailCount}/{target}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Next Steps / Completed Box */}
      {completed ? (
        <Box sx={styles.earnedCreditCompleted}>
          <Typography variant="h6" gutterBottom>
            <Box
              component="i"
              className="fa-regular fa-circle-check"
              sx={{ mr: 0.5 }}
            />{" "}
            Challenge Completed
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "white",
            }}
          >
            You've earned $30 in site credit! It will be auto-applied towards
            your next invoice.
          </Typography>
        </Box>
      ) : (
        displayActionText && (
          <>
            <StyledLargeButton
              fullWidth
              onClick={() => {
                trackEvent("Gamification - Send More Emails Button Clicked");
                navigate(`/${routes.brands}`);
              }}
            >
              Send more emails{" "}
              <Box
                sx={{ ml: 0.5 }}
                component="i"
                className="fa-regular fa-paper-plane"
              />
            </StyledLargeButton>

            <Box sx={styles.nextActionBox}>
              <strong>Next steps:</strong> Send{" "}
              {target - (currentWeek?.emailCount || 0)} emails this week
            </Box>
          </>
        )
      )}
    </Box>
  );
};

export default WeeklyConsistencyChallenge;
